import { useCallback, useEffect, useRef, useState } from 'react';
import {
	SESSION_IDLE_CHECK_INTERVAL_MS,
	SESSION_IDLE_LOCK_MS,
	SESSION_IDLE_LOGOUT_MS,
} from '../constants/sessionIdle';
import {
	getLastActivityAt,
	isSessionLocked,
	setSessionLocked,
	touchActivity,
	clearSessionIdleState,
	SESSION_IDLE_STORAGE_KEYS,
} from '../utils/sessionIdleStorage';

const ACTIVITY_EVENTS = [
	'mousedown',
	'mousemove',
	'keydown',
	'scroll',
	'touchstart',
	'click',
];

const BROADCAST_CHANNEL_NAME = 'session-idle';

const broadcastIdleEvent = (type) => {
	try {
		const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
		channel.postMessage({ type });
		channel.close();
	} catch {
		// BroadcastChannel may be unavailable in some environments.
	}
};

export const useIdleSession = ({ enabled, onLogout }) => {
	const [isLocked, setIsLocked] = useState(() => enabled && isSessionLocked());
	const isLockedRef = useRef(isLocked);
	const onLogoutRef = useRef(onLogout);

	useEffect(() => {
		isLockedRef.current = isLocked;
	}, [isLocked]);

	useEffect(() => {
		onLogoutRef.current = onLogout;
	}, [onLogout]);

	const evaluateIdleState = useCallback(() => {
		if (!enabled) return;

		const idleMs = Date.now() - getLastActivityAt();

		if (idleMs >= SESSION_IDLE_LOGOUT_MS) {
			clearSessionIdleState();
			broadcastIdleEvent('logout');
			onLogoutRef.current?.();
			return;
		}

		if (idleMs >= SESSION_IDLE_LOCK_MS) {
			setSessionLocked(true);
			setIsLocked(true);
			broadcastIdleEvent('locked');
		}
	}, [enabled]);

	useEffect(() => {
		if (!enabled) {
			setIsLocked(false);
			return undefined;
		}

		if (!localStorage.getItem(SESSION_IDLE_STORAGE_KEYS.LAST_ACTIVITY_KEY)) {
			touchActivity();
		}

		evaluateIdleState();

		let throttleTimer = null;
		const onActivity = () => {
			if (isLockedRef.current) return;

			if (throttleTimer) return;
			throttleTimer = window.setTimeout(() => {
				throttleTimer = null;
				touchActivity();
			}, 1000);
		};

		ACTIVITY_EVENTS.forEach((eventName) => {
			window.addEventListener(eventName, onActivity, { passive: true });
		});

		const intervalId = window.setInterval(
			evaluateIdleState,
			SESSION_IDLE_CHECK_INTERVAL_MS,
		);

		let channel;
		try {
			channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
			channel.onmessage = (event) => {
				if (event.data?.type === 'locked') {
					setIsLocked(true);
				}
				if (event.data?.type === 'unlocked') {
					setIsLocked(false);
				}
				if (event.data?.type === 'logout') {
					onLogoutRef.current?.();
				}
			};
		} catch {
			channel = null;
		}

		const onStorage = (event) => {
			if (event.key === SESSION_IDLE_STORAGE_KEYS.SESSION_LOCKED_KEY) {
				setIsLocked(event.newValue === 'true');
			}
			if (event.key === SESSION_IDLE_STORAGE_KEYS.LAST_ACTIVITY_KEY) {
				evaluateIdleState();
			}
		};
		window.addEventListener('storage', onStorage);

		return () => {
			ACTIVITY_EVENTS.forEach((eventName) => {
				window.removeEventListener(eventName, onActivity);
			});
			window.clearInterval(intervalId);
			if (throttleTimer) {
				window.clearTimeout(throttleTimer);
			}
			channel?.close();
			window.removeEventListener('storage', onStorage);
		};
	}, [enabled, evaluateIdleState]);

	const unlock = useCallback(() => {
		touchActivity();
		setSessionLocked(false);
		setIsLocked(false);
		isLockedRef.current = false;
		broadcastIdleEvent('unlocked');
	}, []);

	const reinforceLock = useCallback(() => {
		if (!enabled) return;

		const idleMs = Date.now() - getLastActivityAt();

		if (idleMs >= SESSION_IDLE_LOGOUT_MS) {
			clearSessionIdleState();
			broadcastIdleEvent('logout');
			onLogoutRef.current?.();
			return;
		}

		setSessionLocked(true);
		setIsLocked(true);
		isLockedRef.current = true;
		broadcastIdleEvent('locked');
	}, [enabled]);

	return { isLocked, unlock, reinforceLock };
};
